import { useCallback, useEffect, useRef, useState } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import queryString from 'query-string';

// null and undefined will not be stringified, but are handled by setStorageItemSafely
// and setSearchParamsSafely functions to remove the key from the storage and searchParams.
export type SafelyStringable = object | string | number | boolean | null | undefined;
export type QueryParamStorageState<Value> = [value: Value, setValue: (value: Value) => void];

export function useQueryParamSessionStorageState<Value extends SafelyStringable>(
    key: string,
    defaultValue: Value,
    externalState?: [Value, (value: Value) => void]
): QueryParamStorageState<Value> {
    return useQueryParamStorageState(sessionStorage, key, defaultValue, externalState);
}

export function useQueryParamLocalStorageState<Value extends SafelyStringable>(
    key: string,
    defaultValue: Value,
    externalState?: [Value, (value: Value) => void]
): QueryParamStorageState<Value> {
    return useQueryParamStorageState(localStorage, key, defaultValue, externalState);
}

/**
 * A custom hook that synchronizes a state value with both URL query parameters and web storage (either sessionStorage or localStorage).
 * This hook ensures that the state is kept in sync with both the URL and the specified storage, allowing for persistence and sharing of state across sessions and reloads.
 *
 * @template Value - The type of the state value. It must be safely stringifiable (i.e., can be converted to and from a string).
 *
 * @param {Storage} storage - The web storage object to use (either sessionStorage or localStorage).
 * @param {string} key - The key under which the state value is stored in both the URL query parameters and the web storage.
 * @param {Value} defaultValue - The default value to use if neither the URL query parameters nor the web storage contain a value for the specified key.
 * @param {[Value, (value: Value) => void]} [externalState] - An optional external state tuple. If provided, the hook will use this state instead of its own internal state.
 *
 * @returns {QueryParamStorageState<Value>} A tuple containing the current state value and a function to update the state. The update function will also update the URL query parameters and the web storage.
 *
 * @throws {Error} If the storage key is changed after the initial render, an error is thrown to prevent unpredictable behavior.
 *
 * @example
 * // Using sessionStorage
 * const [value, setValue] = useQueryParamSessionStorageState('myKey', 'defaultValue');
 *
 * // Using localStorage
 * const [value, setValue] = useQueryParamLocalStorageState('myKey', 'defaultValue');
 *
 * // Using external state
 * const externalState = useState('externalValue');
 * const [value, setValue] = useQueryParamLocalStorageState('myKey', 'defaultValue', externalState);
 */
function useQueryParamStorageState<Value extends SafelyStringable>(
    storage: Storage,
    key: string,
    defaultValue: Value,
    externalState?: [Value, (value: Value) => void]
): QueryParamStorageState<Value> {
    // Cannot use searchParams from useSearchParams because it has stale values when
    // multiple calls to setSearchParams are made in the same render cycle.
    // https://github.com/remix-run/react-router/issues/9757
    const [_searchParams, setSearchParams] = useSearchParams();

    const initialStorageKeyRef = useRef(key);
    if (initialStorageKeyRef.current !== key) {
        throw new Error(
            `Invalid use of useQueryParamStorageState. Changing the storage key will result in unpredictable behavior.`
        );
    }

    const paramsValue = getParamsValueSafely<Value>(key);
    const storageValue = getStorageValueSafely<Value>(storage, key);
    const initialValue = paramsValue ?? storageValue ?? defaultValue;

    // If externalState was provided, use that; otherwise, use internal state
    const [state, setState] = externalState ?? useState<Value>(initialValue);

    // sync searchParams, storage and state
    useEffect(() => {
        // Case 1: URL params have a value
        if (paramsValue != null) {
            // Update storage if it's out of sync with URL params
            if (storageValue !== paramsValue) {
                setStorageItemSafely(storage, key, paramsValue);
            }
            // Update state if it's null
            if (state == null) {
                setState(paramsValue);
            }
            return;
        }

        // Case 2: Storage has a value but URL params don't
        if (storageValue != null) {
            // Sync URL params with storage value
            setSearchParamsSafely(setSearchParams, key, storageValue);
            // Update state if it's null
            if (state == null) {
                setState(storageValue);
            }
            return;
        }

        // Case 3: Neither URL params nor storage have a value
        // Use default value if state is null
        if (state == null) {
            setState(defaultValue);
        }
    }, [key, paramsValue, setSearchParams, storageValue, storage, state, defaultValue]);

    const setParamStorageState = useCallback(
        (value: Value) => {
            setState(value);
            setStorageItemSafely(storage, key, value);
            setSearchParamsSafely(setSearchParams, key, value);
        },
        [storage, setSearchParams, key]
    );

    return [state, setParamStorageState];
}

function getParamsValueSafely<Value extends SafelyStringable>(key: string): Value | null {
    try {
        const rawSearch = window.location.search;
        if (!rawSearch) return null;

        // Parse the current query string into an object using query-string
        const parsedParams = queryString.parse(rawSearch, {
            arrayFormat: 'bracket', // arrays -> key[]=val1&key[]=val2
            parseBooleans: true, // convert "true"/"false" to boolean
            parseNumbers: true, // convert numeric strings to numbers
        });

        if (!(key in parsedParams)) return null;

        const rawValue = parsedParams[key];
        if (rawValue == null) return null;

        // If it's a string, try to JSON-parse it:
        if (typeof rawValue === 'string') {
            try {
                return JSON.parse(rawValue) as Value;
            } catch {
                // Fall back to returning it as a plain string
                return rawValue as unknown as Value;
            }
        }

        // Otherwise, return the value as-is (e.g., array or number)
        return rawValue as Value;
    } catch (error) {
        console.error('Failed to parse query param', error);
        return null;
    }
}

function getStorageValueSafely<Value extends SafelyStringable>(storage: Storage, key: string): Value | null {
    try {
        const storageValue = storage.getItem(key);
        return storageValue == null ? null : JSON.parse(storageValue);
    } catch (error) {
        console.error('Failed to parse storage value', error);
        return null;
    }
}

function setSearchParamsSafely<Value extends SafelyStringable>(
    setSearchParams: SetURLSearchParams,
    key: string,
    value: Value
): void {
    try {
        const rawSearch = window.location.search;
        const parsedParams = queryString.parse(rawSearch, {
            arrayFormat: 'bracket',
            parseBooleans: true,
            parseNumbers: true,
        });

        if (value == null) {
            delete parsedParams[key];
        } else if (Array.isArray(value)) {
            // For arrays, store directly (query-string will handle bracket notation)
            parsedParams[key] = value;
        } else if (typeof value === 'object') {
            // JSON-stringify objects
            parsedParams[key] = JSON.stringify(value);
        } else {
            // Store plain strings and other primitive types as is
            parsedParams[key] = value;
        }

        const updatedQuery = queryString.stringify(parsedParams, {
            arrayFormat: 'bracket', // Ensures arrays and nested objects become bracketed
            encode: true,
        });

        setSearchParams(updatedQuery);
    } catch (error) {
        console.error('Error in setting search params', error);
    }
}

function setStorageItemSafely<Value extends SafelyStringable>(storage: Storage, key: string, value: Value): void {
    try {
        if (value != null) {
            const stringifiedValue = JSON.stringify(value);
            storage.setItem(key, stringifiedValue);
        } else {
            storage.removeItem(key);
        }
    } catch (error) {
        console.error('Error in setting storage', error);
    }
}
